import { t } from '@trimble/i18n';
import { StationNavigationController } from '@trimble/web3d-capabilities';
import { Accessor, JSX, Show } from 'solid-js';
import { BasicButton } from '../toolbar/toolbar-controls/BasicButton';
import { CustomInput } from '@tcweb/connect-ui';

/**
 * Controls how many navigation elements are visible.
 *
 * - 'xlarge' — reserved for future Play + Time additions; renders
 *   identically to 'large' today.
 * - 'large'  — all four nav buttons (first/prev/next/last) + station
 *   number input.
 * - 'medium' — prev/next buttons + station number input (first/last
 *   hidden).
 * - 'small'  — prev/next buttons only (input hidden).
 *
 * Callers that embed this control in a flex bar should pick a size
 * based on the available width to avoid overflow.
 */
export type StationNavigationSize = 'xlarge' | 'large' | 'medium' | 'small';

export const StationNavigation = (props: {
  stationNavigationCapability: Accessor<
    StationNavigationController | undefined
  >;
  showCloseButton?: boolean;
  /** Defaults to 'large'. @see StationNavigationSize */
  size?: StationNavigationSize;
}): JSX.Element => {
  const cap = (): StationNavigationController =>
    props.stationNavigationCapability()!;

  const size = (): StationNavigationSize => props.size ?? 'large';

  const showFirstLast = (): boolean =>
    size() === 'xlarge' || size() === 'large';

  const showInput = (): boolean => size() !== 'small';

  const stationDisplayValue = (): string => {
    const index = cap().selectedStationIndex();
    return index != null ? String(index + 1) : '';
  };

  return (
    <>
      <Show when={showFirstLast()}>
        <div class="contents" data-testid="first-station">
          <BasicButton
            iconName="first_page"
            action={() => cap().goToFirstStation()}
            disabled={!cap().isEnabled() || cap().isFirstStation()}
            tooltip={t('web3D.toolbar.actions.firstStation')}
            size="small"
          />
        </div>
      </Show>
      <div class="contents" data-testid="previous-station">
        <BasicButton
          iconName="chevron_left"
          action={() => cap().goToPreviousStation()}
          disabled={!cap().isEnabled() || cap().isFirstStation()}
          tooltip={t('web3D.toolbar.actions.previousStation')}
          size="small"
        />
      </div>
      <div class="contents" data-testid="next-station">
        <BasicButton
          iconName="chevron_right"
          action={() => cap().goToNextStation()}
          disabled={!cap().isEnabled() || cap().isLastStation()}
          tooltip={t('web3D.toolbar.actions.nextStation')}
          size="small"
        />
      </div>
      <Show when={showFirstLast()}>
        <div class="contents" data-testid="last-station">
          <BasicButton
            iconName="last_page"
            action={() => cap().goToLastStation()}
            disabled={!cap().isEnabled() || cap().isLastStation()}
            tooltip={t('web3D.toolbar.actions.lastStation')}
            size="small"
          />
        </div>
      </Show>
      <Show when={showInput()}>
        <modus-wc-divider class="h-6 mx-1" />
        <CustomInput
          name="station-number"
          label=""
          type="number"
          onInputRef={(ref) => ref && (ref.min = '1')}
          onInput={(
            e: InputEvent & { currentTarget: HTMLInputElement; target: Element }
          ) => {
            const value = Number(e.currentTarget.value);
            if (value < 1) {
              e.currentTarget.value = stationDisplayValue();
              return;
            }
            if (
              cap().isLastStation() &&
              value > Number(stationDisplayValue())
            ) {
              e.currentTarget.value = stationDisplayValue();
              return;
            }
            cap().goToStation(value - 1);
          }}
          class="w-20 [&_input]:!min-h-8 [&_input]:!py-0"
          value={stationDisplayValue()}
          disabled={!cap().isEnabled()}
          data-cy="station-number-input"
        />
      </Show>
      <Show when={props.showCloseButton ?? true}>
        <modus-wc-divider class="h-6 mx-1" />
        <div class="contents" data-testid="exit-station-nav">
          <BasicButton
            iconName="close"
            action={() => cap().deactivate()}
            disabled={!cap().isActive()}
            tooltip={t('web3D.toolbar.actions.exitStationNavigation')}
            size="small"
          />
        </div>
      </Show>
    </>
  );
};
